import type { ColorProp, StyleProp } from "../bridge/selection";
import { isBoolean, isNumber, isString } from "@/common/general";
import { EmptyGuid, stringifyGuid } from "@/common/lib/string";
import {
  createFixedBuffer,
  createResizableBuffer,
  extendResizableBuffer,
  RESIZABLE_BUFFER_SIZE,
} from "../interop/bufferManager";

const CURRENT_FILE_VERSION = {
  major: 10,
  minor: 0,
  build: 4,
  revision: 0,
};

enum ObjectStreamIds {
  KeyedDictionary = 1,
  OrderedList = 2,
  InternalObjectReference = 3,
  BinaryData = 4,
  ScalarInteger = 5,
  ScalarDouble = 6,
  ScalarSingle = 7,
  ScalarString = 8,
  ObjectRoot = 9,
  StringTable = 10,
  StringTableId = 11,
  StringTableString = 12,
  LegacyPackageContextBegin = 13,
  LegacyPackageContextEnd = 14,
  RootObjectId = 15,
  PackageReference = 16,
  ScalarGuid = 17,
  RootObjectPackageId = 18,
  PackageId = 19,
  WeakObjectReference = 20,
  ExternalObjectReference = 21,
  ObjectId = 22,
  LegacyTextBlock = 23,
  LegacyRichTextBlock = 24,
  ScalarBoolean = 25,
  PackageContext = 26,
  FileStart = 27,
  FileEnd = 28,
  ScalarLong = 29,
  GuidList = 30,
  StringList = 31,
  // 50000
  DataVersionPre6_0_1 = 0xC350,
  DataVersion6_0_1 = 0xC351,

  // Right now we are only using a few of the bits for the enums so using this to strip higher bits and fix one form of corruption,
  // specifically I had a file where the first bit (the sign bit) was flipped and broke a page, chopping it off fixed it.
  // eslint-disable-next-line ts/no-duplicate-enum-values
  Any = 0x0000001F,
}

enum ScalarType {
  String,
  Integer,
  Single,
  Double,
  Guid,
  Boolean,
  Long,
}

export interface Version {
  major: number;
  minor: number;
  build: number;
  revision: number;
}

export type Keyable = number | string;
export type Saveable = Keyable | boolean | ArrayBuffer | KeyedDictionary | OrderedList | ColorProp | StyleProp<any> | undefined;
// eslint-disable-next-line ts/consistent-type-definitions
export type KeyedDictionary = {
  [name: Keyable]: Saveable;
};
export type OrderedList = Saveable[];

class ReaderState {
  // public buffer: ArrayBuffer;
  public view: DataView;
  public index = 0;
  public stringList = [] as (string | undefined)[];
  public guidList = [] as string[];
  public textDecoder = new TextDecoder();

  public constructor(view: DataView) {
    this.view = view;
  }

  public getInt32() {
    const val = this.view.getInt32(this.index, true);
    this.index += 4;
    return val;
  }

  public getDouble() {
    const val = this.view.getFloat64(this.index, true);
    this.index += 8;
    return val;
  }

  public getInt64() {
    const littleEndian = true;
    const left = this.view.getInt32(this.index, true);
    const right = this.view.getInt32(this.index, true);
    this.index += 8;
    const combined = littleEndian ? left + 2 ** 32 * right : 2 ** 32 * left + right;
    if (!Number.isSafeInteger(combined)) {
      console.warn(combined, "exceeds MAX_SAFE_INTEGER. Precision may be lost");
    }
    return combined;
  }

  public getFloat() {
    const val = this.view.getFloat32(this.index, true);
    this.index += 4;
    return val;
  }

  public getBoolean() {
    const val = this.view.getUint8(this.index);
    this.index += 1;
    return Boolean(val);
  }

  public getString(len: number) {
    const subView = new DataView(this.view.buffer, this.view.byteOffset + this.index, len);
    const str = this.textDecoder.decode(subView);
    this.index += len;
    return str;
  }

  public getGuid() {
    const subView = new Uint8Array(this.view.buffer, this.view.byteOffset + this.index, 16);
    const val = stringifyGuid(subView);
    this.index += 16;
    return val;
  }

  public getBinaryData(len: number) {
    const buffer = new ArrayBuffer(len);
    const bufferView = new Uint8Array(buffer);
    const src = new Uint8Array(this.view.buffer, this.view.byteOffset + this.index, len);
    bufferView.set(src);
    this.index += len;
    return buffer;
  }
}

function loadError(message?: string) {
  throw new Error(message ?? "Invalid context.");
}

function getVersion(state: ReaderState): Version {
  const major = state.getInt32();
  const minor = state.getInt32();
  const build = state.getInt32();
  const revision = state.getInt32();
  return {
    major,
    minor,
    build,
    revision,
  };
}

function getDataVersion(state: ReaderState) {
  return state.getInt32();
}

function loadStringList(state: ReaderState) {
  state.stringList.push(undefined);
  const count = state.getInt32();
  for (let i = 0; i < count; i++) {
    const len = state.getInt32();
    const str = state.getString(len);
    state.stringList.push(str);
  }
}

function loadGuidList(state: ReaderState) {
  state.guidList.push(EmptyGuid);
  const count = state.getInt32();
  for (let i = 0; i < count; i++) {
    state.guidList.push(state.getGuid());
  }
}

function loadString(state: ReaderState) {
  const index = state.getInt32();
  return state.stringList[index];
}

function loadGuid(state: ReaderState) {
  const index = state.getInt32();
  return state.guidList[index];
}

function loadScalar(state: ReaderState, type: ScalarType) {
  switch (type) {
    case ScalarType.Double: return state.getDouble();
    case ScalarType.Integer: return state.getInt32();
    case ScalarType.Long: return state.getInt64();
    case ScalarType.Single: return state.getFloat();
    case ScalarType.String: return loadString(state);
    case ScalarType.Guid: return loadGuid(state);
    case ScalarType.Boolean: return state.getBoolean();
    default: throw new Error("Unrecognized Scalar type");
  }
}

function loadBinaryData(state: ReaderState) {
  const len = state.getInt32();
  return state.getBinaryData(len);
}

function loadKeyedDictionary(state: ReaderState) {
  const val = {} as KeyedDictionary;
  const len = state.getInt32();
  for (let i = 0; i < len; i++) {
    const key = loadSaveable(state, undefined, true) as Keyable;
    val[key] = loadSaveable(state);
  }

  return val;
}

function loadOrderedList(state: ReaderState) {
  const val = [] as OrderedList;
  const len = state.getInt32();
  for (let i = 0; i < len; i++) {
    val.push(loadSaveable(state));
  }
  return val;
}

function loadSaveable<T>(state: ReaderState, streamId?: number, isKey?: boolean): T | Saveable {
  const streamIdVal = streamId ?? state.getInt32();
  switch (streamIdVal) {
    case ObjectStreamIds.WeakObjectReference:
    case ObjectStreamIds.InternalObjectReference:
    case ObjectStreamIds.PackageReference:
      throw new Error("Not supported in javascript");
    case ObjectStreamIds.ScalarDouble: return loadScalar(state, ScalarType.Double);
    case ObjectStreamIds.ScalarInteger: return loadScalar(state, ScalarType.Integer);
    case ObjectStreamIds.ScalarLong: return loadScalar(state, ScalarType.Long);
    case ObjectStreamIds.ScalarSingle: return loadScalar(state, ScalarType.Single);
    case ObjectStreamIds.ScalarString: return loadScalar(state, ScalarType.String);
    case ObjectStreamIds.ScalarGuid: return loadScalar(state, ScalarType.Guid);
    case ObjectStreamIds.ScalarBoolean:
      if (isKey) throw new Error("Cannot use a boolean as a Key");
      return loadScalar(state, ScalarType.Boolean);
    case ObjectStreamIds.BinaryData: return loadBinaryData(state);
    case ObjectStreamIds.KeyedDictionary: return loadKeyedDictionary(state);
    case ObjectStreamIds.OrderedList: return loadOrderedList(state);
    case ObjectStreamIds.PackageId:
    case ObjectStreamIds.ExternalObjectReference:
    case ObjectStreamIds.ObjectId:
    case ObjectStreamIds.LegacyRichTextBlock:
    case ObjectStreamIds.LegacyTextBlock:
      throw new Error("Not supported in javascript");
    default: {
      // eslint-disable-next-line no-bitwise
      const correctId = streamIdVal & ObjectStreamIds.Any;
      if (correctId !== streamIdVal) return loadSaveable(state, correctId);
      throw new Error("Unrecognized saveable type.");
    }
  }
}

export function read<T = Saveable>(data: DataView) {
  const state = new ReaderState(data);
  const root = state.getInt32();
  if (root !== ObjectStreamIds.FileStart) loadError(`Expected Root, got ${root}`);
  const version = getVersion(state);
  const dataVersion = getDataVersion(state);
  let next = state.getInt32();
  if (next !== ObjectStreamIds.StringList) loadError(`Expected StringList, got ${next}`);
  loadStringList(state);
  next = state.getInt32();
  if (next !== ObjectStreamIds.GuidList) loadError(`Expected GuidList, got ${next}`);
  loadGuidList(state);
  next = state.getInt32();
  if (next === ObjectStreamIds.PackageContext) loadError("Package contexts are not supported in Javascript");
  const payload = loadSaveable<T>(state, next) as T;
  return { version, dataVersion, payload };
}

enum WriterPart {
  header, body,
}
class WriterState {
  public bodyBufferId: number;
  public headerBufferId: number | undefined;
  public bodyViews = [] as DataView[];
  public lastBodyViewLength: number = 0;
  // public headerViews = [] as DataView[];
  public headerView?: DataView;
  public currentView: DataView;
  public currentViewIndex = 0;
  public currentPart = WriterPart.body;

  public textEncoder = new TextEncoder();

  public helperBuffer = new ArrayBuffer(16);
  public intHelper = new Uint32Array(this.helperBuffer, 0, 1);
  public doubleHelper = new Float64Array(this.helperBuffer, 0, 1);
  public binaryHelper = new Uint8Array(this.helperBuffer);

  public stringTable = [] as string[];
  public stringMap = {} as { [name: string]: number | undefined };

  public setUint8(val: number) {
    const spaceLeft = RESIZABLE_BUFFER_SIZE - this.currentViewIndex;
    if (spaceLeft === 0) this.addView();
    this.currentView.setUint8(this.currentViewIndex, val);
    this.currentViewIndex += 1;
  }

  public setInt32(val: number) {
    const spaceLeft = RESIZABLE_BUFFER_SIZE - this.currentViewIndex;
    if (spaceLeft >= 4) {
      this.currentView.setInt32(this.currentViewIndex, val, true);
      this.currentViewIndex += 4;
    } else {
      this.intHelper[0] = val;
      this.writeSpannedValue(4);
    }
  }

  private writeSpannedValue(len: number) {
    const spaceLeft = RESIZABLE_BUFFER_SIZE - this.currentViewIndex;
    console.assert(spaceLeft < len, "Should not be writing a spanned value if there enough space left on the current view.");
    if (spaceLeft > 0) {
      const firstTarget = new Uint8Array(this.currentView.buffer, this.currentView.byteOffset, spaceLeft);
      firstTarget.set(this.binaryHelper);
    }
    this.addView();
    const target = new Uint8Array(this.currentView.buffer, this.currentView.byteOffset, len - spaceLeft);
    target.set(this.binaryHelper);
  }

  public getStringIndex(val: string) {
    let index = this.stringMap[val];
    if (index !== undefined) return index;
    // index = this.stringTable.length;
    this.stringTable.push(val);
    index = this.stringTable.length;
    this.stringMap[val] = index;
    return index;
  }

  public setDouble(val: number) {
    if ((RESIZABLE_BUFFER_SIZE - this.currentViewIndex) >= 8) {
      this.currentView.setFloat64(this.currentViewIndex, val, true);
      this.currentViewIndex += 8;
    } else {
      this.doubleHelper[0] = val;
      this.writeSpannedValue(8);
    }
  }

  public setBinaryData(_: Uint8Array) {
    throw new Error("Not Implemented.");
  }

  addView() {
    // these two lines are for if we use a resizable buffer for the header too.
    // const bufferId = this.currentPart === WriterPart.body ? this.bodyBufferId : this.headerBufferId;
    // const viewList = this.currentPart === WriterPart.body ? this.bodyViews : this.headerViews;
    const bufferId = this.bodyBufferId;
    const viewList = this.bodyViews;
    const view = extendResizableBuffer(bufferId);
    viewList.push(view);
    this.currentView = view;
    this.currentViewIndex = 0;
  }

  beginHeaderWrite(allocationSize: number) {
    this.lastBodyViewLength = this.currentViewIndex;
    this.currentPart = WriterPart.header;
    // const val = createResizableBuffer();
    const val = createFixedBuffer(allocationSize);
    this.headerBufferId = val.id;
    this.currentView = val.view;
    // this.headerViews.push(val.view);
    this.headerView = val.view;
    this.currentViewIndex = 0;
  }

  public constructor() {
    const vals = createResizableBuffer();
    this.bodyBufferId = vals.id;
    this.bodyViews.push(vals.view);
    this.currentView = vals.view;
  }
}

function writeNumber(state: WriterState, s: number) {
  state.setInt32(ObjectStreamIds.ScalarDouble);
  state.setDouble(s);
}

function writeString(state: WriterState, s: string) {
  state.setInt32(ObjectStreamIds.ScalarString);
  const index = state.getStringIndex(s);
  state.setInt32(index);
}

function writeBool(state: WriterState, s: boolean) {
  state.setInt32(ObjectStreamIds.ScalarBoolean);
  state.setUint8(s ? 1 : 0);
}

function writeOrderedList(state: WriterState, s: OrderedList) {
  state.setInt32(ObjectStreamIds.OrderedList);
  state.setInt32(s.length);
  for (let i = 0; i < s.length; i++) writeSaveable(state, s[i]);
}

function writeData(state: WriterState, s: Uint8Array) {
  state.setInt32(ObjectStreamIds.BinaryData);
  state.setInt32(s.length);
  state.setBinaryData(s);
}

function writeKeyedDictionary(state: WriterState, s: KeyedDictionary) {
  state.setInt32(ObjectStreamIds.KeyedDictionary);

  const entries = Object.entries(s);

  // skip null/undefined entries and set correct length first
  const validEntries = entries.filter(([_, value]) => value != null);
  state.setInt32(validEntries.length);

  for (const [key, value] of validEntries) {
    writeSaveable(state, key);
    writeSaveable(state, value);
  }
}

function writeSaveable(state: WriterState, s: Saveable) {
  if (s == null) {
    throw new Error("Writing null or undefined to saveable isn't supported. This key/value pair should be skipped instead.");
  }
  if (isString(s)) writeString(state, s);
  else if (isNumber(s)) writeNumber(state, s);
  else if (isBoolean(s)) writeBool(state, s);
  else if (Array.isArray(s)) writeOrderedList(state, s);
  else if (s instanceof Uint8Array) writeData(state, s);
  else writeKeyedDictionary(state, s as KeyedDictionary);
}

function stringListSizeEstimate(state: WriterState) {
  const table = state.stringTable;
  let size = table.length * 8;
  for (const item of table) {
    size += item.length * 2;
  }
  // heuristically, we'll just multiply this by 1.6
  return Math.round(size * 1.6);
}

function writeStringList(state: WriterState) {
  state.setInt32(ObjectStreamIds.StringList);
  state.setInt32(state.stringTable.length);
  for (const stringVal of state.stringTable) {
    // const stringBytes = state.textEncoder.encode(state.stringTable[i]);
    // state.setInt32(stringBytes.length);
    // state.setBinaryData(stringBytes);
    const headerView = state.headerView!;
    if (stringVal.length !== 0) {
      const result = state.textEncoder.encodeInto(
        stringVal,
        new Uint8Array(headerView.buffer, headerView.byteOffset + state.currentViewIndex + 4, headerView.byteLength - state.currentViewIndex - 4),
      );
      if (result.read !== stringVal.length || !result.written) throw new Error(`Did not get back the number of bytes written. for val ${stringVal}`);
      state.setInt32(result.written);
      state.currentViewIndex += result.written;
    } else {
      state.setInt32(0);
    }
  }
}

function writeGuidList(state: WriterState) {
  state.setInt32(ObjectStreamIds.GuidList);
  state.setInt32(0);
}

function writeHeader(state: WriterState) {
  state.setInt32(ObjectStreamIds.FileStart);
  state.setInt32(CURRENT_FILE_VERSION.major);
  state.setInt32(CURRENT_FILE_VERSION.minor);
  state.setInt32(CURRENT_FILE_VERSION.build);
  state.setInt32(CURRENT_FILE_VERSION.revision);
  state.setInt32(ObjectStreamIds.DataVersion6_0_1);
  writeStringList(state);
  writeGuidList(state);
}

export function write(data: Saveable) {
  // we need to write the body first to collect the string table and then we can write the header.
  const state = new WriterState();
  writeSaveable(state, data);
  state.setInt32(ObjectStreamIds.FileEnd);

  const headerSize = stringListSizeEstimate(state) + 500; // might want to account for guid list too if we support it.
  state.beginHeaderWrite(headerSize);
  writeHeader(state);

  return {
    headerBufferId: state.headerBufferId!,
    headerSize: state.currentViewIndex,
    bodyBufferId: state.bodyBufferId,
    bodySize: state.bodyViews.length * RESIZABLE_BUFFER_SIZE + state.lastBodyViewLength,
  };
}

// headerBufferId, headerSize, bodyBufferId, bodySize
export type BufferInfo = [number, number, number, number];

// writes and returns array ready to be passed through JS interop
export function writeForInterop(data: Saveable): BufferInfo {
  const descriptor = write(data);
  return [descriptor.headerBufferId, descriptor.headerSize, descriptor.bodyBufferId, descriptor.bodySize];
}
